import { FormikProvider, useFormik } from 'formik-2';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Translate, withLocalize } from 'react-localize-redux';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import SubPageHeader from '../../components/molecules/SubPageHeader';
import { ConfirmChangesDialog, useNavigationBlocker } from '../../components/organisms/NavigationBlocker';
import { Tabs, WarningMessage } from '../../components/widgets';
import {
    ATTRIBUTION_TAB_IDS,
    FORM_INFO_GUIDES,
    FORM_TABS,
    INITIAL_FORM_DATA,
    INTEGRATION_CARDS,
    STATUSES,
    TRANSLATE_PREFIX,
} from '../consts';
import { getErrorAnchor } from '../errorAnchors';
import { getFormTypeRules } from '../formTypeRules';
import { FieldValidityProvider, useFieldValidityValues } from '../useFieldValidity';
import { formatDate } from '../utils';
import { TAB_VALIDATORS } from '../validators';
import { getPostbackMacroNetworkName } from '../../selectors/partnerIntegration';
import FormActionBar from './FormActionBar';
import GeneralTab from './GeneralTab';
import IntegrationIcon from './IntegrationIcon';
import css from './IntegrationRequestForm.css';
import PostbackOverallSetupTab from './PostbackOverallSetupTab';
import PostbackTemplatesTab from './PostbackTemplatesTab';
import StatusBadge from './StatusBadge';
import TrackingLinkOptionsTab from './TrackingLinkOptionsTab';

const BASE_PATH = '/react/integrations';

const TAB_COMPONENTS = {
    [ATTRIBUTION_TAB_IDS.GENERAL]: GeneralTab,
    [ATTRIBUTION_TAB_IDS.TRACKING_LINK_OPTIONS]: TrackingLinkOptionsTab,
    [ATTRIBUTION_TAB_IDS.POSTBACK_OVERALL_SETUP]: PostbackOverallSetupTab,
    [ATTRIBUTION_TAB_IDS.POSTBACK_TEMPLATES]: PostbackTemplatesTab,
};

const IntegrationRequestForm = ({
    type,
    requestType,
    existingRequest = null,
    readOnly = false,
    onAddRequest,
    onUpdateRequest,
    translate,
}) => {
    const navigate = useNavigate();
    const card = INTEGRATION_CARDS.find(c => c.type === type);
    const titleKey = card ? card.titleKey : '';
    const descriptionKey = card ? card.descriptionKey : '';
    const typeName = card ? translate(card.titleKey) : type;
    const status = existingRequest ? existingRequest.status : STATUSES.NOT_STARTED;
    const isDraftLike = status === STATUSES.NOT_STARTED || status === STATUSES.DRAFT;
    const isLive = status === STATUSES.APPROVED;
    const infoGuide = FORM_INFO_GUIDES[type];
    const formTabs = FORM_TABS[type];

    const [activeTab, setActiveTab] = useState(formTabs?.[0]?.id);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [pendingScrollAnchor, setPendingScrollAnchor] = useState(null);

    const handleErrorClick = (tabId, errorKey) => {
        const { anchor } = getErrorAnchor(errorKey) || {};
        if (tabId !== activeTab) {
            setActiveTab(tabId);
        }
        setPendingScrollAnchor(anchor || null);
    };

    const rules = getFormTypeRules(type);
    const postbackMacroNetworkName = useSelector(getPostbackMacroNetworkName);

    const formik = useFormik({
        initialValues: {
            formData: (() => {
                const initial =
                    INITIAL_FORM_DATA[requestType]?.({
                        postbackMacroNetworkName,
                        isExistingRequest: !!existingRequest,
                    }) || {};
                if (!existingRequest) return initial;
                const saved = existingRequest.form_data || {};
                // Only seed tabs that are currently visible. Anything else (e.g. stale postback
                // keys when receive_postbacks=false) would otherwise leak through to the BE root
                // cross schema, which forbids hidden-tab keys.
                return Object.keys(initial).reduce((acc, tabKey) => {
                    const tab = formTabs?.find(t => t.id === tabKey);
                    if (tab && !rules.isTabVisible(saved, tab)) return acc;
                    acc[tabKey] = { ...initial[tabKey], ...saved[tabKey] };
                    return acc;
                }, {});
            })(),
        },
        validate: values => {
            const tabs = formTabs || [];
            return tabs.reduce((errors, tab) => {
                if (!rules.isTabVisible(values.formData || {}, tab)) return errors;
                const validator = TAB_VALIDATORS[tab.id];
                if (validator) {
                    Object.assign(
                        errors,
                        validator(values.formData?.[tab.id] || {}, values.formData, postbackMacroNetworkName)
                    );
                }
                return errors;
            }, {});
        },
        validateOnMount: true,
        onSubmit: () => {},
    });

    const { allowNextNavigation } = useNavigationBlocker(formik.dirty && !readOnly);

    const formData = formik.values.formData || {};
    const fieldValidity = useFieldValidityValues(formData);

    const handleGoBack = () => {
        if (formik.dirty) {
            setShowCancelDialog(true);
        } else {
            navigate(BASE_PATH);
        }
    };

    const handleConfirmCancel = () => {
        setShowCancelDialog(false);
        allowNextNavigation();
        navigate(BASE_PATH);
    };

    const computedTabs = formTabs
        ?.filter(tab => rules.isTabVisible(formData, tab))
        .map((tab, index) => {
            const disabled = rules.isTabDisabled(formData, tab, index);
            return {
                ...tab,
                disabled,
                tooltipText: disabled ? rules.tabDisabledTooltipKey : undefined,
            };
        });
    const ActiveTabComponent = TAB_COMPONENTS[activeTab];

    // If the active tab was hidden (postbacks turned off), fall back to the last visible tab
    useEffect(() => {
        if (computedTabs && !computedTabs.some(t => t.id === activeTab)) {
            setActiveTab(computedTabs[computedTabs.length - 1]?.id);
        }
    }, [computedTabs, activeTab]);

    useEffect(() => {
        if (!pendingScrollAnchor) return undefined;
        const el = document.querySelector(`[data-error-anchor="${CSS.escape(pendingScrollAnchor)}"]`);
        setPendingScrollAnchor(null);
        if (!el) return undefined;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.dataset.errorFlash = 'true';
        const t = window.setTimeout(() => {
            delete el.dataset.errorFlash;
        }, 1500);
        return () => window.clearTimeout(t);
    }, [activeTab, pendingScrollAnchor]);

    const activeTabIndex = computedTabs?.findIndex(t => t.id === activeTab) ?? -1;
    const nextTab = computedTabs?.[activeTabIndex + 1];

    return (
        <FormikProvider value={formik}>
            <FieldValidityProvider value={fieldValidity}>
                <div className={css.page}>
                    <SubPageHeader
                        onGoBack={handleGoBack}
                        goBackTranslationKey={`${TRANSLATE_PREFIX}.ACTIONS.GO_BACK`}
                        className={css.subPageHeader}
                    />
                    <div className={css.container}>
                        <div className={css.header}>
                            <div className={css.titleSection}>
                                <IntegrationIcon type={type} />
                                <div className={css.titleContent}>
                                    <div className={css.titleRow}>
                                        <span className={css.titleName}>{typeName}</span>
                                        <StatusBadge status={status} />
                                    </div>
                                    {descriptionKey && (
                                        <div className={css.titleDescription}>
                                            <Translate id={descriptionKey} />
                                        </div>
                                    )}
                                </div>
                            </div>
                            {infoGuide && isDraftLike && (
                                <WarningMessage
                                    type="info"
                                    showIcon={false}
                                    className={css.infoGuide}
                                    message={
                                        <div>
                                            <Translate id={infoGuide.introKey} />
                                            <ul className={css.guideList}>
                                                {infoGuide.sections.map(section => (
                                                    <li key={section.titleKey}>
                                                        <span className={css.guideSectionTitle}>
                                                            <Translate id={section.titleKey} />
                                                        </span>{' '}
                                                        <Translate id={section.descriptionKey} />
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    }
                                />
                            )}
                            {/* This message is temporary until we allow updating live integration */}
                            {isLive && (
                                <WarningMessage
                                    type="info"
                                    showIcon={false}
                                    className={css.infoGuide}
                                    message={
                                        <Translate
                                            id={`${TRANSLATE_PREFIX}.LIVE_INFO`}
                                            data={{ submittedAt: formatDate(existingRequest?.submitted_at) || '' }}
                                        />
                                    }
                                />
                            )}
                        </div>
                        {computedTabs && (
                            <Tabs
                                tabs={computedTabs}
                                selected={activeTab}
                                onClick={setActiveTab}
                                numbered
                                containerClass={css.formTabs}
                            />
                        )}
                        <div className={css.formWrapper}>
                            {ActiveTabComponent && <ActiveTabComponent readOnly={readOnly} />}
                            <ConfirmChangesDialog
                                open={showCancelDialog}
                                onAccept={handleConfirmCancel}
                                onReject={() => setShowCancelDialog(false)}
                            />
                        </div>
                        <FormActionBar
                            requestType={requestType}
                            allowNextNavigation={allowNextNavigation}
                            titleKey={titleKey}
                            rules={rules}
                            onAddRequest={onAddRequest}
                            onUpdateRequest={onUpdateRequest}
                            readOnly={readOnly}
                            existingRequest={existingRequest}
                            computedTabs={computedTabs}
                            activeTabIndex={activeTabIndex}
                            nextTab={nextTab}
                            onTabChange={setActiveTab}
                            onErrorClick={handleErrorClick}
                        />
                    </div>
                </div>
            </FieldValidityProvider>
        </FormikProvider>
    );
};

IntegrationRequestForm.propTypes = {
    type: PropTypes.string.isRequired,
    requestType: PropTypes.string.isRequired,
    existingRequest: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        adnetwork_id: PropTypes.number,
        postback_macro_network_name: PropTypes.string,
        form_data: PropTypes.shape({}),
        submitted_at: PropTypes.string,
        status: PropTypes.string,
    }),
    readOnly: PropTypes.bool,
    onAddRequest: PropTypes.func.isRequired,
    onUpdateRequest: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(IntegrationRequestForm);
